import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import MyCkeditor from '@/components/editor/MyCkeditor';
import Fieldset from '@/components/ui/daisy/fieldset';
import useScheduledLessonNotes from '@/data/hook/scheduled-lesson-notes/useScheduledLessonNotes';
import { ImageFolderEnum } from '@/models/CkEditor';
import useFormAutosave from '@/data/hook/shared/useFormAutosave';

const AUTOSAVE_NOTES_TIME_IN_MS = 1500;

export type ScheduledLessonNotesProps = {
  scheduledLessonId: number;
  notes: string;
};

export type ScheduledLessonNotesFields = {
  notes: string;
};

export default function ScheduledLessonNotes({
  scheduledLessonId,
  notes,
}: ScheduledLessonNotesProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'scheduledLessonReportModal',
  });

  const { updateNotes } =
    useScheduledLessonNotes(scheduledLessonId);

  const form = useForm<ScheduledLessonNotesFields>({
    defaultValues: { notes },
  });

  useFormAutosave({
    form,
    onSave: fields => updateNotes(fields.notes),
    delay: AUTOSAVE_NOTES_TIME_IN_MS,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <h2 className="font-semibold leading-7 text-base">{t('notes')}</h2>
      </div>

      <Controller
        name="notes"
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Fieldset labelAlt={error?.message}>
            <MyCkeditor
              testId="notesContent"
              content={value}
              folder={ImageFolderEnum.ACTIVITY}
              handleContentChange={onChange}
              placeholder={t('notesPlaceholder')}
            />
          </Fieldset>
        )}
      />
    </div>
  );
}
